import { useTranslation } from 'react-i18next';
import { useSimpleEventSubscription } from '@passionware/simple-event';
import { GripVertical, Layers, Plus, X } from 'lucide-react';
import { type ComponentProps, forwardRef, type ReactNode } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { KindMap, UnitValues } from 'v5/core/form-schema/formKinds.types';
import { Button } from 'v5/design-system/Button';
import { Input } from 'v5/design-system/Input';
import { focusRings } from 'v5/design-system/_common/focusRing';
import { cn } from 'v5/platform/dom/cn';
import { useRowListFieldArray } from 'v5/patterns/forms/row-list/useRowListFieldArray';
import {
  PLACEHOLDER_ROW_KEY,
  VALUE_PART
} from 'v5/patterns/forms/row-list/useRowListKeyboard';
import { FieldContextProvider, useFieldContext } from './FieldContext';
import { kindListRow } from './ListLinesField';
import type { ListItemsFieldProps } from './ListItemsField';
import { type ListFormRow, newListFormRow } from './listFormRows';
import { RenderField } from './RenderField';
import {
  SortableRows,
  useSortableRow
} from 'v5/patterns/forms/row-list/SortableRows';
import { fieldPath } from './unitPath';
import { usePlaceholderTyping } from './usePlaceholderTyping';

function isBlankValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * The rows of a list unit whose rows hold several fields (an array field
 * group): one block per row, each field a line with its label, behind a tile
 * so a column of tiles shows where every row starts. The layout never
 * depends on the fields' kinds. Edited, the rows share the row-list keyboard
 * (Enter goes to the next row, ⌘⌫ removes one) and an empty block at the end
 * adds a row on the first keystroke.
 */
export function ListBlocksField<M extends KindMap<M>>(
  props: ListItemsFieldProps<M>
) {
  const ctx = useFieldContext();
  return ctx.mode === 'edit' ? (
    <BlocksEditor {...props} />
  ) : (
    <BlocksDisplay {...props} />
  );
}

function BlocksDisplay<M extends KindMap<M>>({
  unit,
  name,
  kit
}: ListItemsFieldProps<M>) {
  const ctx = useFieldContext();
  const rows =
    useWatch<Record<string, ListFormRow<M>[] | undefined>>({ name }) ?? [];
  // An empty list shows only its unit's title and "no value".
  if (rows.length === 0) return null;
  return (
    <div role="list" aria-label={unit.title} className="flex flex-col gap-4">
      {rows.map((row, index) => (
        <Block
          key={row.rowKey}
          role="listitem"
          aria-label={`${unit.item.title} ${index + 1}`}
          tile={<Layers />}
        >
          {/* Print-like at rest: values without their controls' lines. */}
          <FieldContextProvider value={{ ...ctx, variant: 'plain' }}>
            <BlockFields unit={unit} name={name} index={index} kit={kit} />
          </FieldContextProvider>
        </Block>
      ))}
    </div>
  );
}

function BlocksEditor<M extends KindMap<M>>({
  unit,
  name,
  kit,
  newItem,
  savedRowKeys,
  addSignal
}: ListItemsFieldProps<M>) {
  const ctx = useFieldContext();
  const form = useFormContext();
  const firstField = unit.item.fields[0];

  function rowPaths(index: number) {
    return unit.item.fields.map(field =>
      fieldPath(`${name}.${index}.values`, field.key)
    );
  }

  const list = useRowListFieldArray<ListFormRow<M>>({
    name,
    rowKeyOf: row => row.rowKey,
    rowFacts(_row, index) {
      const paths = rowPaths(index);
      const errorAt = paths.findIndex(
        path => form.getFieldState(path).error !== undefined
      );
      return {
        blank: paths.every(path => isBlankValue(form.getValues(path))),
        error: errorAt === -1 ? null : { part: unit.item.fields[errorAt].key }
      };
    },
    onShowError: index => void form.trigger(rowPaths(index))
  });
  const { fields, rows, remove, move } = list;

  function addRow(values: UnitValues<M>) {
    list.add(newListFormRow(values));
  }

  useSimpleEventSubscription(addSignal?.addListener, optionId =>
    addRow(newItem(optionId))
  );

  const fromTyped =
    firstField === undefined
      ? undefined
      : kindListRow(kit, firstField)?.fromTyped;

  // What is typed in the empty block starts a row with it.
  const placeholderTyping = usePlaceholderTyping(
    text => fromTyped?.(text) ?? null,
    typed => {
      if (firstField !== undefined) {
        addRow({ ...newItem(null), [firstField.key]: typed });
      }
    },
    // The row just started is the last one.
    typed => {
      if (firstField !== undefined) {
        form.setValue(
          fieldPath(`${name}.${list.fields.length - 1}.values`, firstField.key),
          typed,
          { shouldDirty: true }
        );
      }
    }
  );

  const regular = ctx.variant === 'regular';

  return (
    <div
      {...list.listProps}
      role="list"
      aria-label={unit.title}
      className={cn('flex flex-col', regular ? 'gap-2 pt-2' : 'pt-1')}
    >
      <SortableRows
        ids={fields.map(field => field.id)}
        itemTitle={unit.item.title}
        onMove={move}
      >
        {fields.map((field, index) => {
          const rowKey = rows[index]?.rowKey ?? field.id;
          return (
            <SortableBlock
              key={field.id}
              id={field.id}
              label={`${unit.item.title} ${index + 1}`}
              sortable={fields.length > 1}
              fresh={savedRowKeys !== undefined && !savedRowKeys.has(rowKey)}
              chrome={regular ? 'box' : 'divided'}
              onRemove={() => remove(index)}
            >
              <BlockFields
                unit={unit}
                name={name}
                index={index}
                kit={kit}
                rowKey={rowKey}
              />
            </SortableBlock>
          );
        })}
      </SortableRows>
      {firstField !== undefined && (
        <Block
          // In the list as its last item, the empty block too.
          role="listitem"
          tile={<Plus />}
          placeholder
          chrome={regular ? 'box' : 'divided'}
        >
          <label className="flex flex-col gap-2">
            <span className="text-base leading-none font-semibold text-fg-muted">
              {firstField.label}
            </span>
            {fromTyped === undefined ? (
              <Button
                type="button"
                variant="secondary"
                size={ctx.size}
                className="self-start"
                aria-label={unit.add.label}
                data-row-key={PLACEHOLDER_ROW_KEY}
                data-row-part={VALUE_PART}
                onClick={() => addRow(newItem(null))}
              >
                <Plus aria-hidden="true" />
                {unit.add.label}
              </Button>
            ) : (
              <Input
                // Re-created per row added, so the next empty block starts blank.
                key={fields.length}
                variant={ctx.variant}
                size={ctx.size}
                autoComplete="off"
                placeholder={unit.add.label}
                aria-label={unit.add.label}
                data-row-key={PLACEHOLDER_ROW_KEY}
                data-row-part={VALUE_PART}
                {...placeholderTyping}
              />
            )}
          </label>
        </Block>
      )}
    </div>
  );
}

type BlockProps = Omit<ComponentProps<'div'>, 'children' | 'ref'> & {
  tile: ReactNode;
  /** Makes the tile the block's drag handle (a button). */
  handle?: ComponentProps<'button'> & { 'aria-label': string };
  children: ReactNode;
  action?: ReactNode;
  /** The empty block at the end: a dashed tile (and box). */
  placeholder?: boolean;
  /** `box` in a create dialog, `divided` (a line under each) in a pane; none at rest. */
  chrome?: 'box' | 'divided';
};

export const Block = forwardRef<HTMLDivElement, BlockProps>(function Block(
  {
    tile,
    handle,
    children,
    action,
    placeholder = false,
    chrome,
    className,
    ...props
  },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(
        'group/block grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3',
        chrome === 'divided' && 'border-b border-border-default py-3',
        chrome === 'box' && 'rounded border border-border-default p-2',
        chrome === 'box' && placeholder && 'border-dashed',
        className
      )}
      {...props}
    >
      {handle === undefined ? (
        <span aria-hidden="true" className={tileClass(placeholder)}>
          {tile}
        </span>
      ) : (
        <button
          type="button"
          aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
          {...handle}
          className={cn(
            tileClass(placeholder),
            'cursor-grab touch-none select-none active:cursor-grabbing',
            'hocus:text-fg-default',
            focusRings.focus.visible
          )}
        >
          {/* The tile shows it can be dragged while the block is hovered or focused, and always on touch. */}
          <span className="grid *:[grid-area:1/1]">
            <span className="flex transition-opacity group-hocus/block:opacity-0 pointer-coarse:opacity-0">
              {tile}
            </span>
            <GripVertical className="opacity-0 transition-opacity group-hocus/block:opacity-100 pointer-coarse:opacity-100" />
          </span>
        </button>
      )}
      <div className="flex min-w-0 flex-col gap-2">{children}</div>
      <div className="flex">{action}</div>
    </div>
  );
});

function tileClass(placeholder: boolean) {
  return cn(
    'flex size-8 shrink-0 items-center justify-center rounded-md border border-border-default text-fg-muted [&_svg]:size-4',
    placeholder ? 'border-dashed' : 'bg-bg-subtle'
  );
}

function SortableBlock({
  id,
  label,
  sortable,
  fresh,
  chrome,
  onRemove,
  children
}: {
  id: string;
  /** "Visa 2". */
  label: string;
  /** Two rows or more: the tile is a handle. */
  sortable: boolean;
  fresh: boolean;
  chrome: 'box' | 'divided';
  onRemove(): void;
  children: ReactNode;
}) {
  const { t } = useTranslation('patterns');
  const { rowProps, handleProps, isDragging } = useSortableRow(id, sortable);
  return (
    <Block
      {...rowProps}
      role="listitem"
      aria-label={label}
      tile={<Layers />}
      handle={
        sortable
          ? { ...handleProps, 'aria-label': t('RowList.reorder', { label }) }
          : undefined
      }
      chrome={chrome}
      className={cn(
        fresh && 'bg-bg-accent/20',
        isDragging && 'bg-bg-default shadow-md'
      )}
      action={
        <Button
          type="button"
          variant="ghost"
          size="sm"
          layout="icon"
          aria-label={t('RowList.remove', { label })}
          // Tab goes on to the next block; ⌘⌫ removes from anywhere in this one.
          tabIndex={-1}
          aria-keyshortcuts="Meta+Backspace Control+Backspace"
          className="text-fg-muted opacity-0 transition-opacity group-hocus/block:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
          onClick={onRemove}
        >
          <X />
        </Button>
      }
    >
      {children}
    </Block>
  );
}

export function BlockFields<M extends KindMap<M>>({
  unit,
  name,
  index,
  kit,
  rowKey
}: Pick<ListItemsFieldProps<M>, 'unit' | 'name' | 'kit'> & {
  index: number;
  /** Edited rows mark each field for the row-list keyboard. */
  rowKey?: string;
}) {
  return (
    <>
      {unit.item.fields.map(field => (
        <div
          key={field.key}
          data-row-key={rowKey}
          data-row-part={rowKey === undefined ? undefined : field.key}
        >
          <RenderField
            kit={kit}
            spec={field}
            path={fieldPath(`${name}.${index}.values`, field.key)}
          />
        </div>
      ))}
    </>
  );
}
